import { Controller } from "@hotwired/stimulus"
import { savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"

// Resource calendar controller — weekly 7×48 half-hour grid showing
// how services are scheduled across the week. Mirrors the iframe
// editor's resourceCalendar tab (pcp_builder.html#5717) at the data
// level so .sdp files round-trip.
//
// Plan shape (kept identical to the iframe editor):
//   plan.resourceCalendar = {
//     enabled: boolean,
//     slots: [{ day: 0..6, startHour: float, endHour: float,
//               resourceRef: "service text" }],
//     notes: "..."
//   }
//
// MVP scope: click-drag paint, single-resource active palette,
// 30-min slot resolution, weekly view. Right-click clears a slot.
// "Monthly" totals view and the iframe editor's drag-resize chips
// are deferred — only the underlying slot model matters for parity
// with .sdp files.

const STORAGE_KEY = "pcp_plan_data"
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const HALF_HOURS = 48
const BUILT_IN_SERVICES = ["In Parental Care", "Charter Home School"]
// School name from the About section (about.schoolDetails) — used for the
// built-in "school" slot. Falls back to the generic "Charter Home School".
function schoolNameFor(plan) {
  const items = (plan && plan.about && plan.about.schoolDetails) || []
  for (const it of items) {
    const raw = (typeof it === "string" ? it : (it && (it.text || it.description)) || "").trim()
    if (raw.length >= 2) return raw.replace(/\s*[,(].*$/, "").trim() || raw
  }
  return "Charter Home School"
}
const PALETTE = [
  "#3b82f6","#22c55e","#a855f7","#f59e0b","#ec4899","#14b8a6",
  "#6366f1","#f97316","#06b6d4","#84cc16","#f43f5e","#0ea5e9"
]

// Canonical service abbreviations — mirrors pcp_builder.html#2127
// SERVICE_ABBREV. Anything outside the map falls back to the first
// four letters so legend chips and grid cells stay narrow.
const SERVICE_ABBREV = {
  "IHSS - In-Home Supportive Services": "IH",
  "IHSS – In-Home Supportive Services": "IH",
  "Personal assistance Services":       "PA",
  "Personal Assistance Services":       "PA",
  "Respite Services - In-Home":         "RS",
  "Respite Services – In-Home":         "RS",
  "Respite Services - Out-of-Home":     "RO",
  "Day Program":                        "DP",
  "Transportation":                     "Tr",
  "Behavioral Therapy":                 "BT",
  "Behavior Services":                  "BS",
  "Behavior Management Services":       "BM",
  "Speech Therapy":                     "ST",
  "Occupational Therapy":               "OT",
  "Occupational Therapy (OT)":          "OT",
  "Physical Therapy":                   "PT",
  "Physical Therapy (PT)":              "PT",
  "Social Recreation":                  "SR",
  "Adaptive Swimming":                  "AS",
  "Tutoring Services":                  "Tutr",
  "Service Coordination":               "SC",
  "Parental Care":                      "PC",
  "In Parental Care":                   "PC",
  "School":                             "SCH",
  "Charter Home School":                "SCH",
  "Music Class":                        "MC",
  "Music Lessons":                      "MC",
  "Art Class":                          "AC",
  "Dance Class":                        "DC",
  "Dance class":                        "DC",
  "Dance Lessons":                      "DC",
  "Art Lessons":                        "AC",
  "Swimming Lessons":                   "SW",
  "Day Camp":                           "DYC",
  "Overnight Camp":                     "ONC",
  "Respite":                            "RS",
  "Personal Assistance":                "PA",
  "Other":                              "Othr"
}
const TBD_RX = /\bTBD\b/i

export default class extends Controller {
  static targets = ["grid", "palette", "totals", "monthlyBtn", "notes",
                    "picker", "pickerLabel", "pickerList"]

  connect() {
    this.plan = this._load()
    if (!this.plan.resourceCalendar) this.plan.resourceCalendar = { enabled: true, slots: [], notes: "" }
    if (!Array.isArray(this.plan.resourceCalendar.slots)) this.plan.resourceCalendar.slots = []
    // Drag-select state. startDay/startSi mark where the mouse went
    // down; currentDay/currentSi follow as the user drags. Released
    // on cellMouseUp, which PAINTS the range with the currently active
    // service (set by the wizard pane). If no active service, mouseup
    // is a no-op — the wizard's legend is the only way to set the
    // brush.
    this.drag = null
    // The active service the wizard pane has selected (via the
    // `rc:service-select` event). Special value "__rc_clear__" means
    // "eraser mode" — drag-mouseup clears every slot in the range.
    this.activeService = null
    // Day order corresponds to the indexes the grid emits in data-day:
    // DAYS = [Mon, Tue, Wed, Thu, Fri, Sat, Sun] → 0..6 contiguous.
    this.dayOrder = [0, 1, 2, 3, 4, 5, 6]
    // Weekly / Monthly toggle for the legend hours-per-service pill.
    // Now lives in the wizard pane — kept here as a fallback when
    // someone still has the old palette markup.
    this.monthly = false
    if (this.hasPaletteTarget) this._buildPalette()
    this._buildGrid()
    this._render()
    // Notes textarea — hydrate from plan, auto-grow + TBD on
    // first paint.
    if (this.hasNotesTarget) {
      this.notesTarget.value = this.plan.resourceCalendar.notes || ""
      this._refreshNotes()
    }
    // Mouseup anywhere ends a drag (even if the user releases outside
    // the grid). cellMouseUp on a slot paints the range; this is the
    // safety net for an off-grid release.
    this._onMouseUp = (e) => {
      if (this.drag && !e.target.closest('.rc-slot')) {
        this._cancelDrag()
      }
    }
    document.addEventListener("mouseup", this._onMouseUp)
    // The wizard pane's service legend dispatches this event when the
    // user picks a service chip (or deselects with a second click).
    // Detail.service is the service name, "__rc_clear__" for the
    // eraser, or null when nothing is active. If there's a pending
    // grid selection (a drag that finished with mouse-up), commit it
    // immediately with the chosen service. Otherwise just remember
    // the active service for next time.
    this._onRcService = (e) => {
      const svc = e.detail?.service || null
      this.activeService = svc
      if (svc && this._pendingRange) {
        const { days, startSi, endSi } = this._pendingRange
        this._applyRange(days, startSi, endSi)
        this._clearPending()
      }
    }
    document.addEventListener("rc:service-select", this._onRcService)
    // "Auto" button on the wizard's school legend chip → paint the school
    // Start/End blocks (computed by the wizard from A Day in the Life) onto
    // the grid. Replaces any existing slots for that service first so
    // re-running is idempotent.
    this._onRcAutoSchool = (e) => {
      const svc = e.detail?.service
      const blocks = Array.isArray(e.detail?.blocks) ? e.detail.blocks : []
      if (!svc || !blocks.length) return
      this.plan = this._load()
      if (!this.plan.resourceCalendar) this.plan.resourceCalendar = { enabled: true, slots: [], notes: "" }
      if (!Array.isArray(this.plan.resourceCalendar.slots)) this.plan.resourceCalendar.slots = []
      this.plan.resourceCalendar.slots = this.plan.resourceCalendar.slots.filter(s => s.resourceRef !== svc)
      blocks.forEach((b, i) => {
        const day = DAYS.indexOf(b.dayName)
        const startHour = parseFloat(b.startHour)
        const endHour   = parseFloat(b.endHour)
        if (day < 0 || !(endHour > startHour)) return
        this.plan.resourceCalendar.slots.push({
          id: Date.now() + Math.random() + i,
          day, startHour, endHour, resourceRef: svc
        })
      })
      this._save()
      this._render()
      this._dispatch()
    }
    document.addEventListener("rc:auto-add-school", this._onRcAutoSchool)
    // Listen for changes to currentServices / unmetNeeds so the legacy
    // in-section palette (if present) updates as the user adds
    // services elsewhere.
    this._onPlan = (e) => {
      const p = e.detail?.path || ""
      if (p === "currentServices" || p === "unmetNeeds" || p === "goals" || p === "barriers" ||
          p === "resourceCalendar.abbrev" ||
          p.startsWith("currentServices.") || p.startsWith("unmetNeeds.") ||
          p.startsWith("goals") || p.startsWith("barriers")) {
        // Reuse the already-parsed plan from the event; only fall back to a
        // full re-parse when no plan rode along on the change.
        this.plan = e.detail.plan || this._load()
        if (this.hasPaletteTarget) this._buildPalette()
        this._render()
      }
    }
    document.addEventListener("plan:change", this._onPlan)
  }

  disconnect() {
    document.removeEventListener("mouseup", this._onMouseUp)
    document.removeEventListener("rc:service-select", this._onRcService)
    document.removeEventListener("rc:auto-add-school", this._onRcAutoSchool)
    document.removeEventListener("plan:change", this._onPlan)
  }

  // Palette chips are legend-only now (the picker is the way to assign
  // services). This action is wired so a chip click still highlights
  // the matching slots — useful for quickly seeing where a service
  // is scheduled.
  pickService(event) {
    const key = event.currentTarget.dataset.serviceKey
    this.activeService = key === this.activeService ? null : key
    this._reflectPalette()
    this._highlightServiceSlots()
  }

  cellMouseDown(event) {
    // Right-click is handled via contextmenu — bail here so we don't
    // start a drag on the right button.
    if (event.button !== 0) return
    event.preventDefault()
    // Starting a new drag discards any previous pending selection that
    // wasn't committed via a wizard chip click.
    this._clearPending()
    const { day, slot } = event.currentTarget.dataset
    const dayI = parseInt(day, 10), slotI = parseInt(slot, 10)
    this.drag = { startDay: dayI, startSi: slotI, currentDay: dayI, currentSi: slotI }
    this._paintDragHighlight()
  }

  cellMouseEnter(event) {
    if (!this.drag) return
    const { day, slot } = event.currentTarget.dataset
    this.drag.currentDay = parseInt(day, 10)
    this.drag.currentSi  = parseInt(slot, 10)
    this._paintDragHighlight()
  }

  cellMouseUp(event) {
    if (!this.drag) return
    const { day, slot } = event.currentTarget.dataset
    const endDay = parseInt(day, 10)
    const endSi  = parseInt(slot, 10)
    const moved  = (this.drag.startDay !== endDay) || (this.drag.startSi !== endSi)
    // Static click on a chip — let the chip's own click handler do the
    // remove; don't set a pending range or apply anything.
    if (!moved && event.target.closest(".rc-cell-chip")) {
      this._cancelDrag()
      return
    }
    const days   = this._dayRange(this.drag.startDay, endDay)
    const minSi  = Math.min(this.drag.startSi, endSi)
    const maxSi  = Math.max(this.drag.startSi, endSi) + 1  // exclusive
    this.drag = null
    // If a service is already active in the wizard pane, commit
    // immediately — feels natural for the pick-then-drag flow.
    if (this.activeService) {
      this._clearPending()
      this._applyRange(days, minSi, maxSi)
      return
    }
    // Otherwise KEEP the blue highlight — the selection persists
    // until the user picks a service in the wizard pane (which fires
    // _applyRange via _onRcService) or starts a new drag (which
    // replaces the pending range).
    this._pendingRange = { days, startSi: minSi, endSi: maxSi }
    this._paintPendingHighlight()
  }

  // Right-click clears any persisted selection — used to bail out
  // without applying. The wizard's legend is the only way to commit a
  // service to the pending range.
  cellContextMenu(event) {
    event.preventDefault()
    this._clearPending()
  }

  // Applies the currently active service to every (day, si) in the
  // range. Mirrors the legacy addCalendarSlotMulti
  // (pcp_builder.html#12047): toggle behavior — if the active service
  // ALREADY fully covers the range, remove just that service's
  // coverage; otherwise append a new slot per day (allowing the cell
  // to hold multiple overlapping services). Eraser clears every
  // service in the range.
  _applyRange(days, startSi, endSi) {
    if (!this.activeService) return
    if (this.activeService === "__rc_clear__") {
      let removed = false
      days.forEach(d => {
        for (let si = startSi; si < endSi; si++) {
          if (this._clearAtCellSilent(d, si)) removed = true
        }
      })
      if (!removed) return
    } else if (this._rangeFullyCoveredBy(this.activeService, days, startSi, endSi)) {
      // Toggle off: shrink/split slots so the chosen service no longer
      // covers ANY cell in the range. Other services on the same cells
      // are untouched.
      this._clearRangeFor(days, startSi, endSi, this.activeService)
    } else {
      days.forEach(d => {
        this.plan.resourceCalendar.slots.push({
          id: Date.now() + Math.random(),
          day: d,
          startHour: startSi / 2,
          endHour:   endSi / 2,
          resourceRef: this.activeService
        })
      })
    }
    this._save()
    this._render()
    this._dispatch()
  }

  // Click a chip inside a cell to remove that single hour of that
  // single slot. Single-slot → drop entirely; edge → shrink; middle
  // → split into before/after. Mirrors legacy removeCalendarSlotHour
  // (pcp_builder.html#12121).
  removeSlotHour(event) {
    event.preventDefault()
    event.stopPropagation()
    const id = event.currentTarget.dataset.slotId
    const si = parseInt(event.currentTarget.dataset.si, 10)
    const slots = this.plan.resourceCalendar.slots || []
    const idx = slots.findIndex(s => String(s.id) === id)
    if (idx === -1) return
    const s = slots[idx]
    const startSi = Math.round((s.startHour || 0) * 2)
    const endSi   = Math.round((s.endHour   || 0) * 2)
    if (endSi - startSi <= 1) {
      slots.splice(idx, 1)
    } else if (si === startSi) {
      s.startHour = (si + 1) / 2
    } else if (si === endSi - 1) {
      s.endHour = si / 2
    } else {
      const after = {
        id: Date.now() + Math.random(),
        day: s.day,
        startHour: (si + 1) / 2,
        endHour:   s.endHour,
        resourceRef: s.resourceRef
      }
      s.endHour = si / 2
      slots.push(after)
    }
    this._save()
    this._render()
    this._dispatch()
  }

  // Removes coverage of `service` from every (day, si) in [si0, si1)
  // by shrinking or splitting existing slots. Mirrors legacy
  // _rcClearRangeFor (pcp_builder.html#12080).
  _clearRangeFor(days, si0, si1, service) {
    const slots = this.plan.resourceCalendar.slots || []
    const next = []
    slots.forEach(s => {
      if (!days.includes(s.day) || s.resourceRef !== service) { next.push(s); return }
      const a = Math.round((s.startHour || 0) * 2)
      const b = Math.round((s.endHour   || 0) * 2)
      if (b <= si0 || a >= si1) { next.push(s); return }
      if (a < si0) next.push({ ...s, id: Date.now() + Math.random(), startHour: a / 2, endHour: si0 / 2 })
      if (b > si1) next.push({ ...s, id: Date.now() + Math.random(), startHour: si1 / 2, endHour: b   / 2 })
    })
    this.plan.resourceCalendar.slots = next
  }

  noteChanged(event) {
    this.plan.resourceCalendar.notes = event.currentTarget.value
    this._save()
    this._refreshNotes()
    this._dispatch()
  }

  // Weekly ↔ Monthly toggle for the legend hours-per-service pill,
  // mirroring the inline button at pcp_builder.html#5752. Multiplies
  // weekly totals by 31/7 for the monthly view (matches the legacy
  // 31-day month approximation).
  toggleMonthly(event) {
    event?.preventDefault?.()
    this.monthly = !this.monthly
    this._buildPalette()
    if (this.hasMonthlyBtnTarget) {
      this.monthlyBtnTarget.textContent = this.monthly ? "Monthly" : "Weekly"
      this.monthlyBtnTarget.classList.toggle("is-monthly", this.monthly)
    }
  }

  // ---- Picker actions ----
  // Wired from the picker's service buttons. Assigns the chosen
  // service to every (day, si) cell in the currently-pending range.
  pickerService(event) {
    event.stopPropagation()
    const ref = event.currentTarget.dataset.serviceRef
    if (!ref) return
    if (!this._pickerRange) return
    const { days, startSi, endSi } = this._pickerRange
    days.forEach(d => {
      // Clear anything already in this range for this day, then push
      // one slot covering the full range so it renders as a single
      // contiguous block with the abbreviation in the first cell.
      for (let si = startSi; si < endSi; si++) {
        this._clearAtCellSilent(d, si)
      }
      this.plan.resourceCalendar.slots.push({
        id: Date.now() + Math.random(),
        day: d,
        startHour: startSi / 2,
        endHour:   endSi   / 2,
        resourceRef: ref
      })
    })
    this._save()
    this._render()
    this._dispatch()
    this._closePicker()
  }

  // Clears every slot inside the pending range and closes the picker.
  pickerClearRange(event) {
    event.stopPropagation()
    if (!this._pickerRange) return
    const { days, startSi, endSi } = this._pickerRange
    let removed = false
    days.forEach(d => {
      for (let si = startSi; si < endSi; si++) {
        if (this._clearAtCellSilent(d, si)) removed = true
      }
    })
    if (removed) {
      this._save()
      this._render()
      this._dispatch()
    }
    this._closePicker()
  }

  pickerClose(event) {
    event?.stopPropagation?.()
    this._closePicker()
  }

  // Empty handler — wired on the picker container so a click INSIDE
  // it doesn't bubble up to the document mousedown listener that
  // closes the picker on outside clicks.
  pickerSwallow(event) {
    event.stopPropagation()
  }

  // ---- internals ----
  // Removes any slot covering (day, si) without triggering save/render
  // (used inside the picker actions which save once at the end).
  // Returns true if anything was removed.
  _clearAtCellSilent(day, slot) {
    const before = this.plan.resourceCalendar.slots.length
    this.plan.resourceCalendar.slots = this.plan.resourceCalendar.slots.filter(s => {
      if (s.day !== day) return true
      const si0 = Math.round((s.startHour || 0) * 2)
      const si1 = Math.round((s.endHour   || 0) * 2)
      return !(slot >= si0 && slot < si1)
    })
    return before !== this.plan.resourceCalendar.slots.length
  }

  // Walks the weekday-ordered day list (Mon → Sun) between two day
  // numbers. Same shape as the legacy rcDragDayRange (pcp_builder.html#11946).
  _dayRange(startDay, endDay) {
    const i1 = this.dayOrder.indexOf(startDay)
    const i2 = this.dayOrder.indexOf(endDay)
    const lo = Math.min(i1, i2), hi = Math.max(i1, i2)
    return this.dayOrder.slice(lo, hi + 1)
  }

  // Re-paints the blue drag-highlight overlay on the cells the user
  // is currently selecting. Mirrors legacy rcHighlightDrag.
  _paintDragHighlight() {
    this.gridTarget.querySelectorAll(".rc-drag-active")
      .forEach(el => el.classList.remove("rc-drag-active"))
    if (!this.drag) return
    const days  = this._dayRange(this.drag.startDay, this.drag.currentDay)
    const minSi = Math.min(this.drag.startSi, this.drag.currentSi)
    const maxSi = Math.max(this.drag.startSi, this.drag.currentSi)
    days.forEach(d => {
      for (let si = minSi; si <= maxSi; si++) {
        const cell = this.gridTarget.querySelector(`.rc-slot[data-day="${d}"][data-slot="${si}"]`)
        if (cell) cell.classList.add("rc-drag-active")
      }
    })
  }

  _cancelDrag() {
    this.drag = null
    this.gridTarget.querySelectorAll(".rc-drag-active")
      .forEach(el => el.classList.remove("rc-drag-active"))
  }

  // Re-paints a persistent highlight on the cells in `_pendingRange`
  // (the selection the user finished dragging but hasn't committed
  // yet — they still need to click a service in the wizard pane).
  // Uses the same `.rc-drag-active` class as the in-flight drag so the
  // visual is continuous from drag → idle. _clearPending() and
  // cellMouseDown reset the class before painting again.
  _paintPendingHighlight() {
    this.gridTarget.querySelectorAll(".rc-drag-active")
      .forEach(el => el.classList.remove("rc-drag-active"))
    if (!this._pendingRange) return
    const { days, startSi, endSi } = this._pendingRange
    days.forEach(d => {
      for (let si = startSi; si < endSi; si++) {
        const cell = this.gridTarget.querySelector(`.rc-slot[data-day="${d}"][data-slot="${si}"]`)
        if (cell) cell.classList.add("rc-drag-active")
      }
    })
  }

  _clearPending() {
    this._pendingRange = null
    this.gridTarget.querySelectorAll(".rc-drag-active")
      .forEach(el => el.classList.remove("rc-drag-active"))
  }

  // Pops the picker over the anchor cell, populated with the
  // categorised service list (Current → Unmet → Built-in). Stores the
  // pending range on `this._pickerRange` so the action handlers know
  // where to write.
  _openPicker(days, startSi, endSi, anchorEl) {
    if (!this.hasPickerTarget) return
    this._pickerRange = { days, startSi, endSi }
    // Range label "Mon–Wed 8:00 AM – 10:30 AM"
    const dayNames = { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 0: "Sun" }
    const dayLbl = days.length === 1
      ? dayNames[days[0]]
      : `${dayNames[days[0]]}–${dayNames[days[days.length - 1]]}`
    this.pickerLabelTarget.textContent = `${dayLbl}  ${this._fmtHour(startSi / 2)} – ${this._fmtHour(endSi / 2)}`
    // Build the list grouped by source.
    const services = this._services()
    const groups = [
      { source: "generic",  label: "Generic" },
      { source: "rc",       label: "RC Funded" },
      { source: "unmet",    label: "Unmet Needs" },
      { source: "parental", label: "Built-in" }
    ]
    const html = groups.map(g => {
      const items = services.filter(s => s.source === g.source)
      if (!items.length) return ""
      const itemsHtml = items.map(s => {
        const active = this._rangeFullyCoveredBy(s.text, days, startSi, endSi)
        return `
          <button type="button"
                  class="rc-picker-item${active ? " is-active" : ""}"
                  data-service-ref="${this._esc(s.text)}"
                  data-action="resource-calendar#pickerService"
                  style="--rc-pick-color:${this._colorFor(s.text)}">
            <span class="rc-picker-dot" style="background:${this._colorFor(s.text)};"></span>
            <span class="rc-picker-abbr">${this._esc(this._abbrev(s.text))}</span>
            <span class="rc-picker-name">${this._esc(s.text)}</span>
            ${active ? `<span class="rc-picker-check">&#10003;</span>` : ""}
          </button>`
      }).join("")
      return `<div class="rc-picker-group"><div class="rc-picker-grouplabel">${g.label}</div>${itemsHtml}</div>`
    }).join("")
    this.pickerListTarget.innerHTML = html
    renderIcons(this.element)
    // Show + position next to the anchor cell. Fixed-positioned so it
    // doesn't get clipped by the scroll container.
    this.pickerTarget.hidden = false
    const rect = anchorEl.getBoundingClientRect()
    const pickerRect = this.pickerTarget.getBoundingClientRect()
    let left = rect.right + 8
    let top  = rect.top
    // Keep it on-screen.
    const maxLeft = window.innerWidth - pickerRect.width - 8
    if (left > maxLeft) left = Math.max(8, rect.left - pickerRect.width - 8)
    const maxTop = window.innerHeight - pickerRect.height - 8
    if (top > maxTop) top = Math.max(8, maxTop)
    this.pickerTarget.style.left = `${left}px`
    this.pickerTarget.style.top  = `${top}px`
  }

  _closePicker() {
    if (!this.hasPickerTarget) return
    this.pickerTarget.hidden = true
    this._pickerRange = null
  }

  // Returns true when `service` already covers EVERY (day, si) cell in
  // the pending range. The picker uses this to show a ✓ next to the
  // already-assigned service.
  _rangeFullyCoveredBy(service, days, startSi, endSi) {
    const slots = this.plan.resourceCalendar.slots || []
    return days.every(d => {
      for (let si = startSi; si < endSi; si++) {
        const covered = slots.some(s => {
          if (s.day !== d || s.resourceRef !== service) return false
          const a = Math.round((s.startHour || 0) * 2)
          const b = Math.round((s.endHour   || 0) * 2)
          return si >= a && si < b
        })
        if (!covered) return false
      }
      return true
    })
  }

  // Toggles a brief highlight on every cell containing the currently
  // active palette chip's service — same UX as the legacy chip click
  // which dim-highlights matching cells.
  _highlightServiceSlots() {
    this.gridTarget.querySelectorAll(".rc-slot.is-search-highlight")
      .forEach(el => el.classList.remove("is-search-highlight"))
    if (!this.activeService) return
    const cells = this.gridTarget.querySelectorAll(`.rc-slot[data-resource-ref="${CSS.escape(this.activeService)}"]`)
    cells.forEach(c => c.classList.add("is-search-highlight"))
  }

  _fmtHour(h) {
    const m = (h * 60) % 60
    const mm = m === 0 ? "" : `:${String(m).padStart(2, "0")}`
    const hr = Math.floor(h)
    if (hr === 0)  return `12${mm} AM`
    if (hr < 12)   return `${hr}${mm} AM`
    if (hr === 12) return `12${mm} PM`
    return `${hr - 12}${mm} PM`
  }

  // Returns [{ text, source }] where source is "parental" for the built-in
  // services, "generic" / "rc" for plan.currentServices (split by their
  // funding tag so the calendar documents Generic vs RC Funded separately),
  // and "unmet" for plan.unmetNeeds (rendered with the dashed amber border
  // + alert-triangle so unmet items stand out — pcp_builder.html#5719).
  _services() {
    const builtin = [
      { text: "Parental Care",          source: "parental" },
      { text: schoolNameFor(this.plan), source: "parental" }
    ]
    const fromCurrent = (this.plan.currentServices || [])
      .map(s => ({ text: this._resolveOtherName((s.linkedType || s.text || "").trim()), source: s.tag === "rc" ? "rc" : "generic" }))
      .filter(s => s.text)
    // UNMET NEEDS come from goal resources tagged "unmet" (fromGoals below) —
    // their specific Service name (r.text). The separate Unmet Needs SECTION
    // (plan.unmetNeeds) holds broad DDS categories ("Case Management",
    // "Socialization, Leisure…") — headings, not service names — so it is
    // intentionally NOT a calendar source.
    // Goal resources (plan.goals[].resources) carry the service NAME in
    // r.text (the "Service name" field) — legacy rows used r.type. Use that,
    // NOT the goal's heading (g.heading), as the calendar label. "Other"
    // resolves to the typed-in r.comment (e.g. "Karate Class") so the grid
    // cell label / color match the wizard legend + the Unmet Needs list.
    const fromGoals = (this.plan.goals || [])
      .flatMap(g => (Array.isArray(g.resources) ? g.resources : []))
      .map(r => ({ text: this._serviceLabel(r), source: r.tag === "rc" ? "rc" : (r.tag === "unmet" ? "unmet" : "generic") }))
      .filter(r => r.text)
    // Barrier resources tagged "unmet" belong on the calendar too (the
    // Unmet Needs list and the wizard legend both include them).
    const fromBarriersUnmet = (this.plan.barriers || [])
      .flatMap(b => (Array.isArray(b.resources) ? b.resources : []))
      .filter(r => r && r.tag === "unmet")
      .map(r => ({ text: this._serviceLabel(r), source: "unmet" }))
      .filter(r => r.text)
    const seen = new Set()
    const out  = []
    ;[...builtin, ...fromCurrent, ...fromGoals, ...fromBarriersUnmet].forEach(s => {
      if (seen.has(s.text)) return
      seen.add(s.text); out.push(s)
    })
    return out
  }

  // Display / paint label for a resource's service — "Other" shows the
  // typed-in r.comment (mirrors the Unmet Needs list + wizard legend) so
  // e.g. "Karate Class" appears instead of the generic "Other".
  _serviceLabel(r) {
    const svc = ((r && (r.text || r.type)) || "").trim()
    if (svc === "Other") return (((r && r.comment) || "").trim()) || "Other"
    return svc
  }
  // A linked current service carries only its TYPE ("Other"); resolve that to
  // the typed-in name from the matching goal/barrier resource comment so the
  // legend/grid read the real name instead of "Other".
  _resolveOtherName(t) {
    if (t !== "Other") return t
    // Exclude UNMET "Other" resources (they belong to Unmet Needs) so a
    // current service resolves to the rc/generic "Other" name.
    const find = (list) => (list || [])
      .flatMap(x => Array.isArray(x.resources) ? x.resources : [])
      .filter(r => r && r.tag !== "unmet" && ((r.type || r.text || "").trim() === "Other") && (r.comment || "").trim())
      .map(r => r.comment.trim())[0]
    return find(this.plan.goals) || find(this.plan.barriers) || "Other"
  }
  // Backward-compat: callers that only need the names.
  _serviceNames() { return this._services().map(s => s.text) }

  _colorFor(service) {
    if (service === "Parental Care") return "#94a3b8"
    const services = this._serviceNames().filter(s => s !== "Parental Care")
    const idx = services.indexOf(service)
    return PALETTE[(idx >= 0 ? idx : 0) % PALETTE.length]
  }

  // Chip ink — black-or-white text chosen from the fill's perceived
  // luminance so the abbreviation reads on BOTH the vibrant fills AND
  // the light ones (slate Parental Care). Returns an inline
  // color + text-shadow that overrides the .rc-cell-chip blanket white
  // per-chip. Mirror of print_preview_controller's rcChipInk.
  _chipInk(bg) {
    const h = String(bg || "").replace("#", "")
    const x = h.length === 3 ? h.split("").map(c => c + c).join("") : h
    const r = parseInt(x.slice(0, 2), 16) || 0
    const g = parseInt(x.slice(2, 4), 16) || 0
    const b = parseInt(x.slice(4, 6), 16) || 0
    return (r * 299 + g * 587 + b * 114) / 1000 > 150
      ? "color:#1f2937;text-shadow:none"
      : "color:#ffffff;text-shadow:0 1px 1.5px rgba(0,0,0,0.5)"
  }

  // Abbreviation lookup: per-plan custom override (set from the wizard
  // legend's edit control) → canonical map → first-4-char fallback. An
  // empty override string means "use the default", so clearing an edit
  // survives the savePlan deep-merge without a delete.
  _abbrev(name) {
    if (!name) return ""
    const custom = (this.plan && this.plan.resourceCalendar && this.plan.resourceCalendar.abbrev) || {}
    if (custom[name]) return String(custom[name])
    if (SERVICE_ABBREV[name]) return SERVICE_ABBREV[name]
    // Any school — whatever it's named in About → School Details
    // (e.g. "Pine Elementary") — abbreviates to SCH.
    if (name === schoolNameFor(this.plan)) return "SCH"
    return name.length > 4 ? name.slice(0, 4) : name
  }

  // Sum hours per service across every slot.
  _hoursMap() {
    const tot = {}
    this.plan.resourceCalendar.slots.forEach(s => {
      const h = (s.endHour || 0) - (s.startHour || 0)
      tot[s.resourceRef] = (tot[s.resourceRef] || 0) + h
    })
    return tot
  }

  // Legend chip — colored pill with the abbreviation in bold, full
  // name, and (when there's data) a weekly/monthly hours pill.
  // Unmet services get a dashed amber border + alert-triangle icon
  // (matches pcp_builder.html#5755-5764). Active picked service
  // gets .is-active so the CSS can ring it.
  _buildPalette() {
    // The legend now lives in the wizard pane — bail when this
    // section partial doesn't render the legacy in-section palette.
    if (!this.hasPaletteTarget) return
    const services = this._services()
    const hrs = this._hoursMap()
    this.paletteTarget.innerHTML = services.map(s => {
      const wk    = hrs[s.text] || 0
      const disp  = this.monthly ? Math.round(wk * (31 / 7)) : wk
      const unit  = this.monthly ? "h/mo" : "h/wk"
      const abbr  = this._abbrev(s.text)
      const hours = wk > 0 ? `<span class="rc-chip-hrs">${disp}${unit}</span>` : ""
      const warn  = s.source === "unmet"
        ? `<i data-lucide="alert-triangle" class="rc-chip-warn"></i>`
        : ""
      return `
        <button type="button"
                class="rc-chip${s.source === "unmet" ? " is-unmet" : ""}"
                data-service-key="${this._esc(s.text)}"
                data-action="resource-calendar#pickService"
                style="background:${this._colorFor(s.text)}">
          ${warn}<strong class="rc-chip-abbr">${this._esc(abbr)}</strong>
          <span class="rc-chip-name">${this._esc(s.text)}</span>
          ${hours}
        </button>`
    }).join("")
    if (this.activeService && !services.find(s => s.text === this.activeService)) {
      this.activeService = null
    }
    this._reflectPalette()
    renderIcons(this.element)
  }

  _reflectPalette() {
    this.paletteTarget.querySelectorAll(".rc-chip").forEach(el => {
      el.classList.toggle("is-active", el.dataset.serviceKey === this.activeService)
    })
  }

  // Re-flow the notes textarea (auto-grow) + apply TBD amber
  // highlight when the value contains the literal "TBD". Matches
  // the inline oninput at pcp_builder.html#5808. Uses the same
  // border-aware + transition-aware scrollHeight math the
  // dynamic-list controller does — see that controller's
  // _autoSize for the full reasoning. Floors at 60px so an empty
  // notes block still reads as a multi-line area.
  _refreshNotes() {
    if (!this.hasNotesTarget) return
    const el = this.notesTarget
    const prevTransition = el.style.transition
    el.style.transition = "none"
    el.style.height = "auto"
    const borderY = (el.offsetHeight - el.clientHeight) || 2
    el.style.height = `${Math.max(el.scrollHeight + borderY, 60)}px`
    void el.offsetHeight
    el.style.transition = prevTransition
    el.classList.toggle("rc-notes-tbd", TBD_RX.test(el.value || ""))
    // ResizeObserver re-runs the resize whenever the textarea's
    // width changes (initial paint, font load, window resize) so
    // the notes block grows to fit wrapped content without
    // needing a manual refresh.
    if (!el._dlSizer && typeof ResizeObserver !== "undefined") {
      let lastWidth = el.offsetWidth
      el._dlSizer = new ResizeObserver(() => {
        const w = el.offsetWidth
        if (w === lastWidth) return
        lastWidth = w
        this._refreshNotes()
      })
      el._dlSizer.observe(el)
    }
  }

  // Builds the 7×48 grid with legacy parity:
  //   - Sticky day-header row
  //   - Whole-hour rows show "8:00 AM" in the time column; half-hour
  //     rows show a small gray ":30"
  //   - Zebra background alternates by HOUR (matches legacy's
  //     Math.floor(si/2) % 2 === 0 ? bg-white : bg-gray-50). Both the
  //     time cell and slot cells in a row carry the same parity class
  //     so the stripe extends across the row.
  //   - Solid bottom border on the second half of each hour (so the
  //     hour boundary is solid); dashed border on the first half
  //     (between hours and the half).
  _buildGrid() {
    const fmtHourLabel = (h) => {
      const hr = Math.floor(h)
      return hr === 0 ? `12:00 AM`
           : hr < 12  ? `${hr}:00 AM`
           : hr === 12 ? `12:00 PM`
           : `${hr - 12}:00 PM`
    }
    let html = `<div class="rc-grid">
      <div class="rc-cell rc-corner"></div>
      ${DAYS.map(d => `<div class="rc-cell rc-day">${d}</div>`).join("")}`
    for (let si = 0; si < HALF_HOURS; si++) {
      const isHalf      = si % 2 === 1
      const hour        = Math.floor(si / 2)
      const hourParity  = hour % 2 === 0 ? "rc-hour-even" : "rc-hour-odd"
      const halfCls     = isHalf ? "is-half" : ""
      const timeLabel   = isHalf
        ? `<span class="rc-time-half">:30</span>`
        : fmtHourLabel(si / 2)
      html += `<div class="rc-cell rc-time ${halfCls} ${hourParity}">${timeLabel}</div>`
      for (let day = 0; day < DAYS.length; day++) {
        html += `<div class="rc-cell rc-slot ${halfCls} ${hourParity}"
                      data-day="${day}" data-slot="${si}"
                      data-action="mousedown->resource-calendar#cellMouseDown mouseenter->resource-calendar#cellMouseEnter mouseup->resource-calendar#cellMouseUp contextmenu->resource-calendar#cellContextMenu"></div>`
      }
    }
    html += `</div>`
    this.gridTarget.innerHTML = html
  }

  // Renders the grid by building a (day, si) → [slot, slot...] lookup
  // and painting one chip per slot per cell. Mirrors the legacy
  // editor's chip-based cell rendering (pcp_builder.html#5965) so a
  // single half-hour cell can hold MULTIPLE overlapping services
  // side-by-side. Each chip is independently click-to-remove via
  // removeSlotHour, which handles single-slot drop / edge shrink /
  // middle split.
  _render() {
    const cells = this.gridTarget.querySelectorAll(".rc-slot")
    cells.forEach(c => {
      c.innerHTML = ""
      c.style.background = ""
      c.removeAttribute("data-resource-ref")
      c.removeAttribute("title")
      c.classList.remove("is-filled", "is-search-highlight")
    })
    const lookup = {}
    this.plan.resourceCalendar.slots.forEach(s => {
      const si0 = Math.round((s.startHour || 0) * 2)
      const si1 = Math.round((s.endHour   || 0) * 2)
      for (let si = si0; si < si1; si++) {
        const k = `${s.day}-${si}`
        ;(lookup[k] ||= []).push(s)
      }
    })
    Object.entries(lookup).forEach(([k, group]) => {
      const [day, si] = k.split("-").map(Number)
      const cell = this.gridTarget.querySelector(`.rc-slot[data-day="${day}"][data-slot="${si}"]`)
      if (!cell) return
      cell.classList.add("is-filled")
      // Single-service title for cells with one chip; comma-list when
      // multiple services overlap.
      cell.setAttribute("title", group.map(s => s.resourceRef).join(", "))
      cell.innerHTML = group.map(s => {
        const color = this._colorFor(s.resourceRef)
        const abbr  = this._abbrev(s.resourceRef)
        return `<span class="rc-cell-chip"
                      style="background:${color};${this._chipInk(color)}"
                      data-slot-id="${this._esc(String(s.id))}"
                      data-si="${si}"
                      data-action="click->resource-calendar#removeSlotHour"
                      title="${this._esc(s.resourceRef)} — click to remove">${this._esc(abbr)}</span>`
      }).join("")
    })
    // Refresh legend chip totals — adding / removing a slot changes
    // hours for that service, so the h/wk pill follows.
    this._buildPalette()
  }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch (_) { return {} }
  }
  _save() {
    // The template seeds resourceCalendar.enabled:false and the native
    // editor has no enable toggle. Once the user has actually scheduled
    // slots, flip it on so the preview / PDF (and any .sdp round-trip)
    // treat the calendar as in use instead of hiding it.
    const rc = this.plan.resourceCalendar
    if (rc && Array.isArray(rc.slots) && rc.slots.length && !rc.enabled) rc.enabled = true
    savePlan(this.plan)
  }
  _dispatch() {
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true, detail: { path: "resourceCalendar.slots", plan: this.plan }
    }))
  }
}
